/**
 * `<metrics-panel>` — `SC-F813`, the instance metrics dashboard.
 *
 * It sits at the bottom of the analytics screen because that is where the question
 * it answers arises: a reader who has just been told every number above comes from
 * audit records asks "so what do the gateway's own metrics say?", and the honest
 * answer is short enough to belong on the same page rather than on one of its own.
 *
 * # The panel is deliberately thin, and it says why on its face
 *
 * `04` §8.1: audit-derived analytics answer every product question; metrics answer
 * only the ones audit structurally cannot. Sluice's catalogue has **no query-latency
 * histogram, no per-query REST counter, no reload counter, no per-datasource health
 * gauge and no budget gauge** — so all five questions a reader opens a metrics page
 * to ask are answered somewhere else, and this panel names the somewhere for each.
 * A page that showed eighteen series and stayed silent about query latency would
 * invite the reader to conclude the gateway has none.
 *
 * # There is no scrape pipeline behind this, and the panel does not imply one
 *
 * `/metrics` sits on Sluice's admin listener behind the per-instance admin bearer
 * token, and Prometheus sets scrape credentials per *job* rather than per target
 * (`SC-F812`, `SLUICE-U07`). So the control plane reads one instance, once, when
 * this panel opens: no collector, no retention, no series history, and every value
 * is a counter since the process started. A shared metrics token across the fleet is
 * refused outright — the metrics token *is* the admin token — and the panel renders
 * that refusal rather than leaving it in a design document.
 *
 * # Absent is not a gap
 *
 * A collector that has never observed an event emits no series, so a missing
 * `sluice_audit_dropped_total` is the GOOD case. Rows the exposition did not carry
 * render as "not yet observed" and are collapsed by default, because a reader
 * scanning nineteen dashes learns nothing.
 *
 * All four states: loading, empty (the instance answered and emits no catalogue
 * family at all — which means `admin.enabled` is false), error (the `apierr` code
 * AND the request id), populated.
 *
 * Stories: `SC-S808` (this surface is why that story's last criterion is testable),
 * `SC-S907`.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import type { BnBadgeVariant, BnColumn, BnKvRow, BnRow } from "../components/index.js";

import {
  getInstanceMetrics,
  type InstanceMetrics,
  type MetricRow,
  type MetricSample,
} from "../api/analytics.js";
import { ApiError, parseError } from "../api/errors.js";

/** The observed table. `value` is last and right-aligned because that is the column
 * a reader scans; `answers` carries the row's whole justification and is therefore
 * the widest. */
const COLUMNS: BnColumn[] = [
  { key: "metric", label: "Metric", mono: true },
  { key: "type", label: "Type", width: "1px" },
  { key: "samples", label: "Value", width: "28%" },
  { key: "answers", label: "What it tells you" },
];

/** The four registered types, tone-mapped. They are all `neutral` on purpose: a
 * type is a shape and not a status, and giving a counter a colour would compete with
 * the one badge on this panel that carries meaning — the availability of the reading
 * itself. */
const TYPE_TONE: Record<MetricRow["type"], BnBadgeVariant> = {
  counter: "neutral",
  gauge: "neutral",
  histogram: "neutral",
  summary: "neutral",
  unknown: "warning",
};

@customElement("metrics-panel")
export class MetricsPanel extends LitElement {
  @property() instanceId = "";

  @state() private report: InstanceMetrics | null = null;
  @state() private loading = true;
  @state() private error: ApiError | null = null;
  /** Whether the rows the instance did not emit are shown. Collapsed by default:
   * a reader scanning nineteen dashes learns nothing, and the count is stated. */
  @state() private showAbsent = false;

  private seq = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .lede {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    h3 {
      margin: var(--bino-space-lg) 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    h3:first-of-type {
      margin-top: 0;
    }
    .meta {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    .row {
      display: flex;
      gap: var(--bino-space-xs);
      flex-wrap: wrap;
      align-items: center;
    }
    .samples {
      display: grid;
      gap: 2px;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      font-variant-numeric: tabular-nums;
    }
    .sample-labels {
      color: var(--bino-text-secondary);
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-muted);
    }
    .gaps {
      margin: 0;
      padding: 0;
      list-style: none;
      display: grid;
      gap: var(--bino-space-md);
    }
    .gaps li {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    .gaps strong {
      color: var(--bino-text);
    }
    details {
      margin-top: var(--bino-space-md);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-sm) var(--bino-space-md);
      background: var(--bino-surface-subtle);
    }
    summary {
      cursor: pointer;
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
    }
    summary:focus-visible {
      outline: var(--bino-focus-ring);
      outline-offset: 2px;
    }
    .posture {
      margin: var(--bino-space-md) 0 0;
      padding: 0;
      list-style: none;
      display: grid;
      gap: var(--bino-space-sm);
    }
    .posture li {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    .posture strong {
      color: var(--bino-text);
    }
    .error-message {
      margin: 0 0 var(--bino-space-sm);
      color: var(--bino-error);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  override willUpdate(changed: Map<string, unknown>) {
    if (this.report === null && this.error === null) return;
    if (changed.has("instanceId")) void this.load();
  }

  /** Reads the exposition. The sequence guard keeps an out-of-order response from a
   * rapid instance switch out of the render, the same way the consumption panel's
   * does. */
  private async load() {
    const mine = ++this.seq;
    this.loading = true;
    try {
      const report = await getInstanceMetrics(this.instanceId);
      if (mine !== this.seq) return;
      this.report = report;
      this.error = null;
    } catch (err) {
      if (mine !== this.seq) return;
      this.error = parseError(err);
      this.report = null;
    } finally {
      if (mine === this.seq) this.loading = false;
    }
  }

  /** One row per emitted catalogue family, or per absent one when the reader has
   * asked to see them. */
  private rows(report: InstanceMetrics): BnRow[] {
    const rows = report.catalogue.filter((r) => this.showAbsent || r.present);
    return rows.map((r) => ({
      id: r.name,
      metric: html`${r.name}
        ${r.also_in_audit
          ? html`<span class="sub">audit records answer this with more detail</span>`
          : nothing}`,
      type: html`<bn-badge .variant=${TYPE_TONE[r.type] ?? "neutral"}>${r.type}</bn-badge>`,
      samples: r.present ? this.renderSamples(r) : html`<span class="sub">not yet observed</span>`,
      answers: html`${r.answers}${r.help
        ? html`<span class="sub">The instance's own help text: ${r.help}</span>`
        : nothing}`,
    }));
  }

  /** Renders a family's series. Labels are printed beside the value rather than in
   * their own column because the label set differs per family, and a column that is
   * empty for two thirds of the rows is worse than an inline pair. */
  private renderSamples(r: MetricRow): TemplateResult {
    const samples = r.samples ?? [];
    return html`<div class="samples">
      ${samples.map(
        (s) => html`<div>
          <span>${s.value}</span>
          ${labelText(s) ? html`<span class="sample-labels"> ${labelText(s)}</span>` : nothing}
        </div>`,
      )}
      ${r.samples_truncated
        ? html`<span class="sub">series list capped — there are more</span>`
        : nothing}
    </div>`;
  }

  /** The build-info row, lifted out of the table because it is identity rather than
   * measurement. Nothing in the control plane branches on it (`08` §24.4:
   * feature-detect, never version-sniff) and the panel says so. */
  private renderBuild(report: InstanceMetrics): TemplateResult | typeof nothing {
    const build = report.build;
    if (!build || Object.keys(build).length === 0) return nothing;
    const rows: BnKvRow[] = Object.keys(build)
      .sort()
      .map((key) => ({ label: key, value: build[key] }));
    return html`<div>
      <h3>Running build</h3>
      <bn-kv .rows=${rows} unsetText="not reported"></bn-kv>
      <p class="meta">
        Recorded for display and for the compatibility table. Nothing in Sluice Cloud branches on a
        version string — capabilities are feature-detected, because a version is a guess about a
        build that might carry backports while a <code>501</code> is a fact.
      </p>
    </div>`;
  }

  /** The five questions the catalogue structurally cannot answer, each with the
   * surface that does. This block is the reason the panel exists in this shape. */
  private renderGaps(report: InstanceMetrics): TemplateResult {
    return html`<div>
      <h3>What metrics cannot tell you here</h3>
      <ul class="gaps">
        ${report.absent_signals.map(
          (s) => html`<li>
            <strong>${s.signal}</strong> — ${s.why} <em>Answered by:</em> ${s.answered_by}
            <span class="sub">${s.feature}</span>
          </li>`,
        )}
      </ul>
    </div>`;
  }

  /** The posture disclosure: how the reading was obtained, what is not built, and
   * the option that is refused outright. Rendered verbatim from the API so the screen
   * and the server cannot drift into two accounts of the same decision. */
  private renderPosture(report: InstanceMetrics): TemplateResult {
    const p = report.posture;
    return html`<details>
      <summary>How this reading was obtained, and what is deliberately not built</summary>
      <ul class="posture">
        <li><strong>Transport.</strong> ${p.transport}</li>
        <li><strong>Credential.</strong> ${p.credential}</li>
        <li><strong>No scrape configuration.</strong> ${p.no_scrape_job}</li>
        <li><strong>Refused outright.</strong> ${p.rejected}</li>
        <li><strong>The upstream change that would fix it.</strong> ${p.upstream}</li>
        <li><strong>Metrics are not audit.</strong> ${p.not_audit}</li>
      </ul>
    </details>`;
  }

  private renderError(err: ApiError): TemplateResult {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return html`<div role="alert">
      <p class="error-message">${err.message}</p>
      <bn-kv .rows=${rows}></bn-kv>
      <p class="meta">
        Every number on the rest of this page is derived from ingested audit records and is
        unaffected: this panel is the only thing here that reaches the instance itself.
      </p>
      <div style="margin-top:var(--bino-space-md)">
        <bn-button @click=${() => void this.load()}>Try again</bn-button>
      </div>
    </div>`;
  }

  /** The unavailable state — the instance did not answer. It is a `200` from the API
   * with an explanation, not an error, and the panel keeps the catalogue and the five
   * gaps on screen because they are facts about the build rather than about the
   * moment. */
  private renderUnavailable(report: InstanceMetrics): TemplateResult {
    return html`<bn-panel eyebrow="The instance's own metrics are unavailable">
      <p class="meta">
        ${report.error?.message ??
        "This control plane could not read the instance's metrics endpoint."}
      </p>
      <p class="meta">
        Nothing above or below this panel depends on it. The catalogue and the gaps listed here are
        properties of the Sluice build we run, so they are stated whether or not the instance
        answered.
      </p>
      ${report.error?.code
        ? html`<bn-kv .rows=${[{ label: "Error code", value: report.error.code }] as BnKvRow[]}></bn-kv>`
        : nothing}
    </bn-panel>`;
  }

  override render() {
    const report = this.report;
    const emitted = report ? report.catalogue.filter((r) => r.present).length : 0;
    const absent = report ? report.catalogue.length - emitted : 0;

    return html`<bn-card heading="The gateway's own metrics">
      <div slot="actions" class="row">
        ${report?.available
          ? html`<bn-badge variant="success">read ${when(report.observed_at)}</bn-badge>`
          : nothing}
        <bn-button size="sm" @click=${() => void this.load()}>Re-read</bn-button>
      </div>

      <p class="lede">
        Sluice's own process counters, read from this instance when you opened the panel. They are
        <strong>not</strong> the source of the analytics above and cannot be: there is no
        query-latency histogram, no per-query counter and no per-datasource gauge in the catalogue.
        Nothing is collected, stored or charted over time — every value is a counter since the
        process started.
      </p>

      ${this.error
        ? this.renderError(this.error)
        : html`
            ${report && !report.available ? this.renderUnavailable(report) : nothing}
            ${report && report.available && emitted === 0
              ? html`<bn-empty-state
                  icon="◔"
                  heading="This build registers no metrics"
                  message="The instance answered and its exposition carries none of the catalogue's families. Sluice gates metric registration on the admin plane being enabled, so this is a configuration fact rather than a fault — and it is the one condition under which the endpoint answers and reports nothing."
                ></bn-empty-state>`
              : html`
                  <bn-table
                    caption="Registered metrics"
                    .columns=${COLUMNS}
                    .rows=${report ? this.rows(report) : []}
                    ?loading=${this.loading}
                  >
                    <div slot="empty">
                      <bn-empty-state
                        icon="∅"
                        heading="Nothing observed yet"
                        message="No catalogue family has emitted a series. For most of them that is the good case — a collector that has never seen an event emits nothing."
                      ></bn-empty-state>
                    </div>
                  </bn-table>
                  ${absent > 0
                    ? html`<p class="meta">
                        ${absent} of ${report?.catalogue.length ?? 0} registered metrics have emitted
                        no series. For most of them that is the good case rather than a gap.
                        <bn-button
                          size="sm"
                          @click=${() => (this.showAbsent = !this.showAbsent)}
                        >
                          ${this.showAbsent ? "Hide them" : "Show them"}
                        </bn-button>
                      </p>`
                    : nothing}
                `}
            ${report?.truncated
              ? html`<p class="meta">
                  The exposition ended mid-line, so at least one series was lost in transfer. That is
                  a transport artefact rather than a metric that stopped being emitted.
                </p>`
              : nothing}
            ${report?.unrecognized?.length
              ? html`<p class="meta">
                  This instance emits ${report.unrecognized.length} metric
                  ${report.unrecognized.length === 1 ? "family" : "families"} Sluice Cloud does not
                  model: <code>${report.unrecognized.join(", ")}</code>. That is a signal that the
                  pinned Sluice build has moved ahead of our catalogue, not an error.
                </p>`
              : nothing}
            ${report ? this.renderBuild(report) : nothing}
            ${report ? this.renderGaps(report) : nothing}
            ${report ? this.renderPosture(report) : nothing}
          `}
    </bn-card>`;
  }
}

/** labelText renders a sample's labels as `{a="b",c="d"}`, or "" for an unlabelled
 * series. */
function labelText(s: MetricSample): string {
  const labels = s.labels;
  if (!labels) return "";
  const keys = Object.keys(labels).sort();
  if (keys.length === 0) return "";
  return `{${keys.map((k) => `${k}="${labels[k]}"`).join(",")}}`;
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date". */
function when(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "metrics-panel": MetricsPanel;
  }
}
